<template>
  <a-drawer
    :open="open"
    :title="product ? '编辑商品' : '新增商品'"
    width="min(760px, 100vw)"
    :closable="!saving"
    :mask-closable="!saving"
    :keyboard="!saving"
    @close="emit('update:open', false)"
  >
    <a-form
      ref="formRef"
      :model="form"
      :rules="rules"
      layout="vertical"
      :disabled="saving || !canManage"
      @finish="save"
    >
      <h2>基本信息</h2>
      <a-form-item name="kind" label="商品类型" required>
        <a-select
          v-model:value="form.kind"
          :disabled="Boolean(product)"
          :options="productKindOptions"
        />
      </a-form-item>
      <a-form-item name="name" label="商品名称" required>
        <a-input v-model:value="form.name" :maxlength="80" :placeholder="suggestedName" />
        <template #extra
          ><span>建议名称：{{ suggestedName }}</span
          ><a-button type="link" size="small" @click="form.name = suggestedName"
            >填入建议名称</a-button
          ></template
        >
      </a-form-item>
      <a-form-item name="description" label="商品说明"
        ><a-textarea v-model:value="form.description" :rows="2" :maxlength="240" show-count
      /></a-form-item>
      <h2>价格与购买方式</h2>
      <div class="product-grid">
        <a-form-item name="amountYuan" label="售价（元）" required
          ><a-input-number v-model:value="form.amountYuan" :min="0.01" :precision="2"
        /></a-form-item>
        <a-form-item v-if="form.kind === 'pro_plan'" name="purchaseMode" label="购买方式">
          <a-select
            v-model:value="form.purchaseMode"
            :options="[
              { value: 'one_time', label: '固定期限购买' },
              { value: 'auto_renew', label: '自动续费' },
            ]"
          />
        </a-form-item>
        <a-form-item v-if="isRecurring" name="billingCycle" label="续费周期">
          <a-select
            v-model:value="form.billingCycle"
            :options="[
              { value: 'month', label: '按月' },
              { value: 'year', label: '按年' },
            ]"
          />
        </a-form-item>
      </div>
      <h2>商品权益</h2>
      <div class="product-grid">
        <a-form-item
          v-if="hasDuration"
          name="durationDays"
          :label="isRecurring ? '每次续费授予天数' : '有效期（天）'"
          required
        >
          <a-input-number v-model:value="form.durationDays" :min="1" :max="3650" :precision="0" />
        </a-form-item>
        <a-form-item
          v-if="hasSeats"
          name="seatCount"
          :label="form.kind === 'team_plan' ? '套餐包含席位数' : '每件增加席位数'"
          required
        >
          <a-input-number v-model:value="form.seatCount" :min="1" :precision="0" />
        </a-form-item>
        <template v-if="isMembership">
          <a-form-item name="maxWorkspaces" label="最大工作台数" required
            ><a-input-number
              v-model:value="form.maxWorkspaces"
              :min="1"
              :max="10000"
              :precision="0"
          /></a-form-item>
          <a-form-item name="maxComponentInstances" label="单工作台最大组件数" required
            ><a-input-number
              v-model:value="form.maxComponentInstances"
              :min="1"
              :max="100000"
              :precision="0"
          /></a-form-item>
          <a-form-item name="quotaStorageGiB" label="套餐存储额度（GiB）" required
            ><a-input-number
              v-model:value="form.quotaStorageGiB"
              :min="0"
              :max="1024"
              :precision="1"
          /></a-form-item>
          <a-form-item name="quotaTransferGiB" label="每月下载流量（GiB）" required
            ><a-input-number
              v-model:value="form.quotaTransferGiB"
              :min="0"
              :max="10240"
              :precision="1"
          /></a-form-item>
          <a-form-item name="quotaRecordCount" label="套餐数据记录额度（行）" required
            ><a-input-number
              v-model:value="form.quotaRecordCount"
              :min="0"
              :max="100000000"
              :precision="0"
          /></a-form-item>
          <a-form-item name="quotaCredits" label="每周期积点（Credits）" required
            ><a-input-number
              v-model:value="form.quotaCredits"
              :min="0"
              :max="1000000"
              :precision="0"
          /></a-form-item>
        </template>
        <template v-if="form.kind === 'credit_pack'">
          <a-form-item name="credits" label="到账积点（Credits）" required
            ><a-input-number v-model:value="form.credits" :min="1" :precision="0"
          /></a-form-item>
          <a-form-item name="creditsExpiryDays" label="积点有效期（天）" required
            ><a-input-number
              v-model:value="form.creditsExpiryDays"
              :min="1"
              :max="3650"
              :precision="0"
          /></a-form-item>
        </template>
        <a-form-item
          v-if="form.kind === 'storage_pack'"
          name="storageGiB"
          label="扩容容量（GiB）"
          required
          ><a-input-number v-model:value="form.storageGiB" :min="1" :precision="0"
        /></a-form-item>
        <a-form-item
          v-if="form.kind === 'record_pack'"
          name="recordCount"
          label="扩容数据记录数（行）"
          required
          ><a-input-number v-model:value="form.recordCount" :min="1" :precision="0"
        /></a-form-item>
      </div>
      <a-alert
        v-if="form.kind === 'team_plan'"
        type="info"
        show-icon
        message="Team 的积点、存储、流量和数据记录为账号共享额度；增购席位不会增加这些额度。"
      />
      <a-alert
        v-if="form.kind === 'team_seat_pack'"
        type="info"
        show-icon
        message="席位包仅增加当前有效 Team 会员的席位，随该会员到期。"
      />
      <h2>上架与排序</h2>
      <a-form-item
        name="displayOrder"
        label="展示排序"
        extra="数值越小越靠前；后台在同一商品类型内排序。"
        ><a-input-number v-model:value="form.displayOrder" :min="0" :precision="0"
      /></a-form-item>
      <a-form-item label="上架状态"
        ><a-switch
          v-model:checked="form.enabled"
          checked-children="已上架"
          un-checked-children="已下架"
      /></a-form-item>
      <p class="form-note">上架后可在对应购买入口展示，实际支付仍取决于渠道配置。</p>
    </a-form>
    <template #footer
      ><a-space class="drawer-actions"
        ><a-button :disabled="saving" @click="emit('update:open', false)">取消</a-button
        ><a-button v-if="canManage" type="primary" :loading="saving" @click="submit"
          >保存商品</a-button
        ></a-space
      ></template
    >
  </a-drawer>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, watch } from 'vue';
import { message, type FormInstance } from 'ant-design-vue';
import type { Rule } from 'ant-design-vue/es/form';
import type {
  CommerceProductKind,
  CommerceProductView,
  PurchaseMode,
  BillingCycle,
  CommerceProductGrant,
} from 'shared';
import { useAdminSessionStore } from '@/stores/admin-session.store';
import { useCommerceProductsStore } from '@/stores/commerce-products.store';
import {
  productKindOptions,
  suggestedProductName,
  type ProductInput,
} from '@/utils/commerce-products';

const props = defineProps<{ open: boolean; product?: CommerceProductView }>();
const emit = defineEmits<{ 'update:open': [value: boolean] }>();
const store = useCommerceProductsStore();
const session = useAdminSessionStore();
const canManage = computed(() => session.can('commerce.manage'));
const saving = ref(false);
const formRef = ref<FormInstance>();
const defaults = () => ({
  kind: 'pro_plan' as CommerceProductKind,
  name: '',
  description: '',
  amountYuan: 1,
  displayOrder: 0,
  purchaseMode: 'one_time' as PurchaseMode,
  billingCycle: 'month' as BillingCycle,
  durationDays: 30,
  maxWorkspaces: 5,
  maxComponentInstances: 100,
  seatCount: 1,
  credits: 100,
  creditsExpiryDays: 365,
  quotaStorageGiB: 10,
  quotaTransferGiB: 100,
  quotaRecordCount: 500000,
  quotaCredits: 2000,
  storageGiB: 1,
  recordCount: 10000,
  enabled: true,
});
const form = reactive(defaults());
const isMembership = computed(() => form.kind === 'pro_plan' || form.kind === 'team_plan');
const hasSeats = computed(() => form.kind === 'team_plan' || form.kind === 'team_seat_pack');
const hasDuration = computed(() => form.kind !== 'credit_pack' && form.kind !== 'team_seat_pack');
const isRecurring = computed(() => form.kind === 'pro_plan' && form.purchaseMode === 'auto_renew');
const input = computed<ProductInput>(() => {
  let grant: CommerceProductGrant;
  if (isMembership.value) {
    grant = {
      durationDays: form.durationDays,
      capacity: {
        maxWorkspaces: form.maxWorkspaces,
        maxComponentInstances: form.maxComponentInstances,
      },
      quota: {
        storageBytes: Math.round(form.quotaStorageGiB * 1024 ** 3),
        transferBytes: Math.round(form.quotaTransferGiB * 1024 ** 3),
        recordCount: form.quotaRecordCount,
        aiCredits: form.quotaCredits,
      },
      ...(form.kind === 'team_plan' ? { seatCount: form.seatCount } : {}),
    };
  } else if (form.kind === 'credit_pack')
    grant = { credits: form.credits, creditsExpiryDays: form.creditsExpiryDays };
  else if (form.kind === 'storage_pack')
    grant = {
      durationDays: form.durationDays,
      storageBytes: Math.round(form.storageGiB * 1024 ** 3),
    };
  else if (form.kind === 'record_pack')
    grant = { durationDays: form.durationDays, recordCount: form.recordCount };
  else grant = { seatCount: form.seatCount };
  return {
    id: props.product?.id,
    kind: form.kind,
    name: form.name.trim(),
    description: form.description.trim(),
    amountMinor: Math.round(form.amountYuan * 100),
    enabled: form.enabled,
    displayOrder: form.displayOrder,
    purchaseMode: isRecurring.value ? 'auto_renew' : 'one_time',
    billingCycle: isRecurring.value ? form.billingCycle : null,
    grant,
  };
});
const suggestedName = computed(() =>
  suggestedProductName({ ...input.value, id: props.product?.id ?? '', currency: 'CNY' }),
);
function numericRule(label: string, min = 1, max = Number.MAX_SAFE_INTEGER, integer = true): Rule {
  return {
    validator: async (_rule, value) => {
      if (
        typeof value !== 'number' ||
        !Number.isFinite(value) ||
        value < min ||
        value > max ||
        (integer && !Number.isSafeInteger(value))
      ) {
        throw new Error(
          `${label}须填写${min === 0 ? '非负' : '正'}${integer ? '整数' : '数值'}，且不超过 ${max.toLocaleString('zh-CN')}。`,
        );
      }
    },
  };
}
const rules: Record<string, Rule[]> = {
  name: [{ required: true, whitespace: true, max: 80, message: '请填写 1 至 80 字的商品名称。' }],
  description: [{ max: 240, message: '商品说明不能超过 240 字。' }],
  amountYuan: [numericRule('售价', 0.01, Number.MAX_SAFE_INTEGER / 100, false)],
  displayOrder: [numericRule('展示排序', 0)],
  durationDays: [numericRule('有效期', 1, 3650)],
  seatCount: [numericRule('席位数量')],
  credits: [numericRule('到账积点')],
  creditsExpiryDays: [numericRule('积点有效期', 1, 3650)],
  maxWorkspaces: [numericRule('工作台上限', 1, 10000)],
  maxComponentInstances: [numericRule('组件上限', 1, 100000)],
  quotaStorageGiB: [numericRule('存储额度', 0, 1024, false)],
  quotaTransferGiB: [numericRule('下载流量', 0, 10240, false)],
  quotaRecordCount: [numericRule('数据记录额度', 0, 100000000)],
  quotaCredits: [numericRule('周期积点', 0, 1000000)],
  storageGiB: [numericRule('扩容容量', 1, Math.floor(Number.MAX_SAFE_INTEGER / 1024 ** 3), false)],
  recordCount: [numericRule('扩容记录数')],
};
watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    Object.assign(form, defaults());
    if (props.product) {
      const item = props.product;
      const grant = item.grant;
      Object.assign(form, {
        kind: item.kind,
        name: item.name,
        description: item.description,
        amountYuan: item.amountMinor / 100,
        displayOrder: item.displayOrder,
        purchaseMode: item.purchaseMode,
        billingCycle: item.billingCycle ?? 'month',
        enabled: item.enabled,
        durationDays: grant.durationDays ?? 30,
        seatCount: grant.seatCount ?? 1,
        credits: grant.credits ?? 100,
        creditsExpiryDays: grant.creditsExpiryDays ?? 365,
        maxWorkspaces: grant.capacity?.maxWorkspaces ?? (item.kind === 'team_plan' ? 20 : 5),
        maxComponentInstances:
          grant.capacity?.maxComponentInstances ?? (item.kind === 'team_plan' ? 500 : 100),
        quotaStorageGiB: (grant.quota?.storageBytes ?? 10 * 1024 ** 3) / 1024 ** 3,
        quotaTransferGiB: (grant.quota?.transferBytes ?? 100 * 1024 ** 3) / 1024 ** 3,
        quotaRecordCount: grant.quota?.recordCount ?? 500000,
        quotaCredits: grant.quota?.aiCredits ?? 2000,
        storageGiB: (grant.storageBytes ?? 1024 ** 3) / 1024 ** 3,
        recordCount: grant.recordCount ?? 10000,
      });
    }
    await nextTick();
    formRef.value?.clearValidate();
  },
);
watch(
  () => form.kind,
  () => formRef.value?.clearValidate(),
);
async function submit(): Promise<void> {
  if (saving.value || !canManage.value) return;
  try {
    await formRef.value?.validate();
  } catch {
    return;
  }
  await save();
}
async function save(): Promise<void> {
  if (saving.value || !canManage.value) return;
  saving.value = true;
  try {
    await store.save(input.value);
    message.success(props.product ? '商品已更新' : '商品已创建');
    emit('update:open', false);
  } catch (error) {
    message.error(
      error instanceof Error && /[\u4e00-\u9fff]/.test(error.message)
        ? error.message
        : '商品保存失败，请检查网络或商品参数后重试。',
    );
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
h2 {
  margin: 28px 0 16px;
  font-size: 16px;
  font-weight: 600;
}
h2:first-child {
  margin-top: 0;
}
.product-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 20px;
}
.product-grid :deep(.ant-input-number) {
  width: 100%;
}
.form-note {
  color: var(--aw-text-secondary);
}
.drawer-actions {
  display: flex;
  justify-content: flex-end;
}
@media (max-width: 600px) {
  .product-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
